import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, ParallaxImage } from "./Motion";

const YOUTUBE_IMG =
    "https://images.unsplash.com/photo-1594394489098-74ac04c0fc2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CRICKET_IMG =
    "https://images.pexels.com/photos/36741130/pexels-photo-36741130.jpeg?auto=compress&cs=tinysrgb&w=1200";

const RECORDS = [
    {
        label: "Amazon Prime Video — YouTube",
        role: "Cocoma Digital Agency · Content strategy",
        image: YOUTUBE_IMG,
        alt: "Studio video production camera",
        slug: "amazon-prime-youtube",
        stats: [
            { value: "18M → 20.5M", label: "subscribers, Apr 2023 – Apr 2024" },
            { value: "3×", label: "monthly organic views" },
            { value: "300M", label: "organic views every month" },
        ],
    },
    {
        label: "Star Sports · JioStar — Social",
        role: "Full-time role · Design to publishing",
        image: CRICKET_IMG,
        alt: "Packed cricket stadium at night",
        slug: "star-sports-social",
        stats: [
            { value: "7M → 10M", label: "Instagram followers in one year" },
            { value: "5B+", label: "views across social" },
            { value: "End-to-end", label: "campaign design to publishing" },
        ],
    },
];

const BAND = [
    { value: "5.5M+", label: "followers added across YouTube & Instagram" },
    { value: "5B+", label: "views driven across social" },
    { value: "3×", label: "monthly organic views, in one year" },
];

export default function TrackRecord() {
    return (
        <section data-testid="track-record-section" className="relative border-t border-line px-5 md:px-10 py-24 md:py-32 overflow-hidden">
            <Reveal>
                <div className="mb-14 md:mb-20">
                    <p className="eyebrow mb-6 flex items-center gap-3">
                        <span className="h-2 w-2 rounded-full bg-vermilion animate-pulse" /> Track record
                    </p>
                    <h2 className="display-xl font-display font-light text-paper">
                        The receipts<span className="text-vermilion">.</span>
                    </h2>
                    <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-smoke">
                        HVP's principal consultant has done the work at the highest level —
                        inside two of India's biggest content machines.
                    </p>
                </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px border border-line bg-line mb-20 md:mb-28" data-testid="track-record-band">
                {BAND.map((b, i) => (
                    <Reveal key={b.label} delay={i * 0.08} className="bg-ink">
                        <div className="p-8 md:p-10 h-full transition-colors duration-500 hover:bg-card">
                            <p className="font-mono text-4xl md:text-5xl font-light text-vermilion">{b.value}</p>
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-smoke">
                                {b.label}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>

            <div className="flex flex-col gap-20 md:gap-28">
                {RECORDS.map((r, i) => (
                    <Reveal key={r.slug} delay={0.05}>
                        <div className={`grid gap-10 lg:grid-cols-12 lg:gap-16 items-center`}>
                            <Link
                                to={`/work/${r.slug}`}
                                data-testid={`track-record-card-${r.slug}`}
                                className={`group relative block overflow-hidden border border-line lg:col-span-5 ${
                                    i % 2 === 1 ? "lg:order-2" : ""
                                }`}
                            >
                                <ParallaxImage
                                    src={r.image}
                                    alt={r.alt}
                                    className="aspect-[4/3] w-full"
                                    strength={9}
                                />
                                <span className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center bg-ink/60 backdrop-blur-sm transition-colors duration-300 group-hover:bg-vermilion">
                                    <ArrowUpRight className="h-4 w-4 text-paper transition-transform duration-300 group-hover:rotate-45" />
                                </span>
                            </Link>
                            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion mb-3">
                                    {r.role}
                                </p>
                                <Link to={`/work/${r.slug}`} className="block">
                                    <h3 className="display-lg font-display font-light text-paper transition-colors duration-300 hover:text-vermilion">
                                        {r.label}
                                    </h3>
                                </Link>
                                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    {r.stats.map((s) => (
                                        <div key={s.label} className="border-t border-line pt-4">
                                            <p className="font-mono text-xl md:text-2xl text-paper">{s.value}</p>
                                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-smoke">
                                                {s.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke/60" data-testid="track-record-attribution-note">
                * Personal track record — with Amazon Prime Video (via Cocoma Digital Agency) and Star Sports (JioStar)
            </p>
        </section>
    );
}
